<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi, IRowNode } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { theme } from '../../../services/stores/theme-store.js';
  import { projectStructureStore, studyExtractStore } from './stores.js';
  import { userStore } from '../../../services/stores/user-store.js';
  import { updateTargetCell } from './target-table-processing.js';
  import { saveTargetExtract } from '../data/target-data-handler.js';
  import { ColumnHeaderForTarget } from './headerComponents.js';
  import { getCellEditorConfig,  getLookupValueGetterSetter, getLookupCellRenderer } from './cell-editor-config.js';
  import { createCellWithIndicator, DEFAULT_CURRENT_DOCUMENT_ID } from '../indicator-utils.js';
  import { logWarning, logError, logInfo } from '../../../services/logging/log-handler.js';
// @ts-ignore - compiled output resolves to .js extension
import { getDisplayName, getGrouperIds, getCustomValue, getLevelDisplayName } from './grouper-utils.js';
  
  // Props
  export let columns: any[] = []; // Column structure from project structure
  export let rows: any[] = []; // Row data from extract store
  export let data2D: any[][] | null = null; // Alternative: 2D array format (headers + rows)
  export let columnStructure: any[] | null = null; // Optional: Column structure for multilevel headers when using data2D
  export let rowIdMap: Map<number, string> | null = null; // Optional: Map of rowIndex -> rowId when using data2D
  export let topicId: string = '';
  export let tableId: string = '';
  export let showColumnKeys: boolean = true;
  export let grouperData: any[] = []; // New grouper schema rows
  export let onCellValueChanged: ((params: any) => void) | null = null; // Optional callback for cell changes
  export let getSourceInfoForCell: ((rowIndex: number, columnId: string) => any) | null = null; // Optional function to get source info
  export let currentDocumentID: string = DEFAULT_CURRENT_DOCUMENT_ID;
  export let showIndicators: boolean = true; // Whether to show source indicators in cells
  export let domLayout: 'normal' | 'autoHeight' = 'autoHeight'; // Grid layout: 'normal' for internal scrolling, 'autoHeight' for content-based height
export let grouperStructure: any = null; // Grouper structure for ordering composite names
  
  // Grid refs
  let gridDiv: HTMLDivElement;
  let gridApi: GridApi | null = null;
  let gridInstance: any = null;
  
  // Helper to get reference value by collection and ID
  function getReferenceValue(collectionId: string, itemId: string): string {
    const studyExtract = $studyExtractStore;
    if (!studyExtract) return itemId;
    
    const collection = studyExtract?.references?.find((ref: any) => ref.id === collectionId);
    if (!collection) return itemId;
    
    // Handle both 'rows' structure (for tables) and 'values' structure (for objects)
    if (collection.rows) {
      const row = collection.rows.find((r: any) => r.id === itemId);
      if (row?.cells) {
        // Find the 'name' or 'title' cell
        const nameCell = row.cells.find((c: any) => c.id === 'name' || c.id === 'title');
        return nameCell?.value || itemId;
      }
    }
    
    if (collection.values) {
      const value = collection.values.find((v: any) => v.id === itemId);
      return value?.value || itemId;
    }
    
    return itemId;
  }
  
  // Helper to resolve column reference (e.g., grouper_name)
  function resolveColumnReference(
    column: any,
    rowId: string,
    rowIndex: number,
    grouperRows: any[],
    tables: any[],
    options?: {
      preferredRow?: any;
      grouperStructure?: any;
    }
  ): string {
    if (!column.refTable || !column.refID) return '';

    if (column.refTable === 'grouper') {
      if (!Array.isArray(grouperRows)) {
        throw new Error('[grouper] Expected grouper array when resolving column reference');
      }

      let grouperRow = options?.preferredRow || grouperRows.find((r: any) => {
        const identifier = String(r?.rowId ?? r?.id ?? '');
        return identifier === String(rowId);
      });

      if (!grouperRow && rowIndex >= 0 && rowIndex < grouperRows.length) {
        grouperRow = grouperRows[rowIndex];
      }

      if (!grouperRow && typeof rowId === 'string') {
        grouperRow = grouperRows.find((r: any) => {
          const ids = getGrouperIds(r);
          return ids.includes(rowId);
        });
      }

      if (!grouperRow) {
        return '';
      }

      if (column.refID === 'name') {
        if (!$studyExtractStore) {
          return String(grouperRow.rowId ?? grouperRow.id ?? '');
        }
        return getDisplayName(grouperRow, $studyExtractStore, {
          grouperStructure: options?.grouperStructure
        });
      }

      if (column.refID === 'custom' || column.refID === 'Custom') {
        return getCustomValue(grouperRow);
      }

      if (column.level !== undefined && options?.grouperStructure && $studyExtractStore) {
        const targetLevel = (options.grouperStructure.levels || []).find((level: any) => {
          const identifier = String(level.level ?? level.number ?? '');
          return identifier === String(column.level);
        });
        if (targetLevel) {
          return getLevelDisplayName(grouperRow, targetLevel, $studyExtractStore) || '';
        }
      }

      return '';
    }

    const table = tables.find((t: any) => t.id === column.refTable);
    if (!table?.rows) return '';

    const row = table.rows.find((r: any) => String(r.rowId ?? r.id ?? '') === String(rowId));
    if (!row?.cells) return '';

    const cell = row.cells.find((c: any) => c.id === column.refID);
    return cell?.value || '';
  }
  
  function getGroupDisplayValue(grouperRow: any): string {
    if (!grouperRow) return '-';
    if ($studyExtractStore) {
      return getDisplayName(grouperRow, $studyExtractStore, { grouperStructure });
    }
    return String(grouperRow.rowId ?? grouperRow.id ?? '-');
  }

  // Convert columns/rows to columnDefs and rowData
  function createColumnDefsAndRowData(): { columnDefs: any[], rowData: any[] } {
    
    // If data2D is provided, convert it to columns/rows format first
    if (data2D && data2D.length > 0) {
      const headers = data2D[0];
      const dataRows = data2D.slice(1);
      
      
      // Use columnStructure if provided (for multilevel headers), otherwise create simple structure
      const convertedColumns = columnStructure && columnStructure.length > 0 
        ? columnStructure 
        : headers.map((header: string) => ({
            id: header.toLowerCase().replace(/\s+/g, '_'),
            label: header
          }));
      
      
      // Convert to row structure
      const convertedRows = dataRows.map((row: any[], index: number) => {
        // Use rowIdMap if provided, otherwise generate ID
        const rowId = rowIdMap?.get(index) || `r${index + 1}`;
        const rowObj: any = {
          id: rowId,
          cells: row.map((value: any, colIndex: number) => {
            const col = convertedColumns[colIndex];
            return {
              id: col?.id || headers[colIndex]?.toLowerCase().replace(/\s+/g, '_') || `col${colIndex}`,
              value: value || '-'
            };
          })
        };
        return rowObj;
      });
      
      
      return createColumnDefsAndRowDataFromStructure(convertedColumns, convertedRows);
    }
    
    // Use columns/rows structure
    
    if (columns.length === 0) {
      logWarning('TopicGrid: No columns provided', "TopicGrid", undefined);
      return { columnDefs: [], rowData: [] };
    }
    
    return createColumnDefsAndRowDataFromStructure(columns, rows);
  }
  
  function ensureGrouperColumns(cols: any[]): any[] {
    if (!grouperStructure?.levels || !Array.isArray(grouperStructure.levels)) {
      return cols;
    }

    const result = [...cols];
    const levelEntries = grouperStructure.levels.map((level: any, index: number) => ({
      levelNumber: String(level.level ?? level.number ?? index + 1),
      label: level.label || level.id || `Level ${index + 1}`
    }));

    const hasGroupColumn = result.some((col: any) => col.id === 'grouper_name');
    if (!hasGroupColumn) {
      const primaryLabel = levelEntries[0]?.label || 'Group';
      result.unshift({
        id: 'grouper_name',
        label: primaryLabel,
        refTable: 'grouper',
        refID: 'name'
      });
    }

    levelEntries.forEach((levelEntry: { levelNumber: string; label: string }, index: number) => {
      const columnId = `grouper_level_${levelEntry.levelNumber}`;
      const existing = result.find((col: any) => {
        return (
          col.id === columnId ||
          (col.refTable === 'grouper' && String(col.level ?? '') === levelEntry.levelNumber)
        );
      });

      if (existing) {
        existing.refTable = 'grouper';
        existing.level = levelEntry.levelNumber;
        if (!existing.refID) {
          existing.refID = 'level';
        }
        if (!existing.label) {
          existing.label = levelEntry.label;
        }
        return;
      }

      const insertedColumn = {
        id: columnId,
        label: levelEntry.label,
        refTable: 'grouper',
        refID: 'level',
        level: levelEntry.levelNumber
      };

      const anchorId =
        index === 0 ? 'grouper_name' : `grouper_level_${levelEntries[index - 1].levelNumber}`;
      const anchorIndex = result.findIndex((col: any) => col.id === anchorId);
      if (anchorIndex >= 0) {
        result.splice(anchorIndex + 1, 0, insertedColumn);
      } else {
        result.push(insertedColumn);
      }
    });

    return result;
  }

  function createColumnDefsAndRowDataFromStructure(cols: any[], rowData: any[]): { columnDefs: any[], rowData: any[] } {
    const isGrouperGrid = Array.isArray(grouperStructure?.levels) && cols === grouperStructure.levels;
    const processedCols = isGrouperGrid ? ensureGrouperColumns(cols) : cols;

    
    const columnDefs = createColumnDefs(processedCols, isGrouperGrid);
    const agGridRowData = createRowData(rowData, processedCols, isGrouperGrid);
    
    
    return { columnDefs, rowData: agGridRowData };
  }
  
  function createColumnDefs(cols: any[], isGrouperGrid: boolean = false): any[] {
    if (isGrouperGrid) {
      return createGrouperColumnDefs(cols);
    }

    const hasGrouperData = Array.isArray(grouperData) && grouperData.length > 0;
    const filteredCols = cols
      .map((column) => {
        if (column.type === 'group' && Array.isArray(column.fields)) {
          const filteredFields = column.fields.filter((field: any) => field.refTable !== 'grouper');
          if (filteredFields.length === 0) {
            return null;
          }
          return {
            ...column,
            fields: filteredFields
          };
        }
        if (column.refTable === 'grouper') {
          return null;
        }
        return column;
      })
      .filter(Boolean);

    const levelDefinitions = Array.isArray(grouperStructure?.levels) ? grouperStructure.levels : [];
    const primaryLevel = levelDefinitions[0];
    const additionalLevels = levelDefinitions.slice(1).filter((level: any, index: number) => {
      const levelNumber = String(level?.level ?? level?.number ?? index + 2).toLowerCase();
      return levelNumber !== 'custom';
    });

    const derivedGroupColumn = hasGrouperData
      ? [{
          field: '__grouperDisplay',
          headerName: (primaryLevel?.label || 'Group').toUpperCase(),
          editable: false,
          width: 240,
          minWidth: 200,
          maxWidth: 260,
          pinned: 'left',
          valueGetter: (params: any) => {
            const rowId = params.data?.id ?? params.data?.rowId ?? '';
            const rowIndex = params.node?.rowIndex ?? 0;
            const grouperRows = Array.isArray(grouperData) ? grouperData : [];
            const grouperRow = grouperRows.find((row: any) => String(row?.rowId ?? row?.id ?? '') === String(rowId))
              ?? grouperRows[rowIndex];
            return getGroupDisplayValue(grouperRow);
          },
          cellRenderer: (params: any) => params.value || '-',
          cellClass: 'topic-grid__grouper-cell',
          suppressNavigable: true,
          cellFocus: false,
          sortable: false,
          filter: false,
          resizable: false,
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          cellStyle: {
            'white-space': 'normal',
            'word-wrap': 'break-word',
            'overflow-wrap': 'break-word',
            'padding': '8px 8px'
          }
        }] : [];

    const derivedLevelColumns = hasGrouperData
      ? additionalLevels.map((level: any, index: number) => {
          const levelNumber = String(level?.level ?? level?.number ?? index + 2);
          const headerName = (level?.label || `Level ${levelNumber}`).toUpperCase();
          return {
            field: `__grouperLevel_${levelNumber}`,
            headerName,
            editable: false,
            width: 200,
            minWidth: 180,
            cellRenderer: (params: any) => params.value || '-',
            cellClass: 'topic-grid__grouper-cell',
            suppressNavigable: true,
            cellFocus: false,
            sortable: false,
            filter: false,
            resizable: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            cellStyle: {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px'
            }
          };
        })
      : [];

    const dataColumns = filteredCols.map((column, index) => {
      const isFixed = index === 0 && derivedGroupColumn.length === 0;
      const isMultiline = column.multiline === true;
      const editorConfig = getCellEditorConfig(column, $projectStructureStore);
      const lookupValueConfig = getLookupValueGetterSetter(column, $projectStructureStore);
      const lookupRenderer = getLookupCellRenderer(column, $projectStructureStore);

      // Handle column grouping (multilevel headers)
      let children = undefined;
      if (column.type === 'group' && column.fields) {
        children = column.fields.map((field: any) => {
          const fieldMultiline = field.multiline === true;
          const fieldEditorConfig = getCellEditorConfig(field, $projectStructureStore);
          const fieldLookupValueConfig = getLookupValueGetterSetter(field, $projectStructureStore);
          const fieldLookupRenderer = getLookupCellRenderer(field, $projectStructureStore);
          
          return {
            field: field.id,
            headerName: field.label || field.id,
            headerComponent: 'columnHeaderForTarget',
            headerComponentParams: {
              displayName: field.label || field.id,
              key: field.id,
              showColumnKeys: showColumnKeys
            },
            minWidth: fieldMultiline ? 200 : undefined,
            width: fieldMultiline ? 200 : undefined,
            maxWidth: fieldMultiline ? 200 : undefined,
            editable: true,
            ...fieldEditorConfig,
            wrapText: fieldMultiline,
            autoHeight: fieldMultiline && domLayout === 'autoHeight',
            sortable: false,
            filter: false,
            resizable: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            valueGetter: (params: any) => {
              const value = params.data[field.id];
              if (value === '-' || value === undefined || value === null) return '';
              if (fieldLookupValueConfig?.valueGetter) {
                return fieldLookupValueConfig.valueGetter(params);
              }
              return value;
            },
            valueSetter: (params: any) => {
              let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
              if (fieldLookupValueConfig?.valueSetter) {
                return fieldLookupValueConfig.valueSetter(params);
              }
              params.data[field.id] = value;
              return true;
            },
            cellRenderer: (params: any) => {
              let displayValue = params.value || '-';
              if (fieldLookupRenderer && displayValue !== '-') {
                displayValue = fieldLookupRenderer(params) || '-';
              }
              const sourceInfo = params.data?.[`${field.id}_sourceInfo`];
              if (getSourceInfoForCell) {
                const additionalSourceInfo = getSourceInfoForCell(params.node.rowIndex, field.id);
                return showIndicators 
                  ? createCellWithIndicator(displayValue, additionalSourceInfo || sourceInfo, currentDocumentID)
                  : displayValue;
              }
              return showIndicators 
                ? createCellWithIndicator(displayValue, sourceInfo, currentDocumentID)
                : displayValue;
            },
            cellStyle: fieldMultiline ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            } : {
              'white-space': 'nowrap',
              'overflow': 'hidden',
              'text-overflow': 'ellipsis',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            }
          };
        });
      }
      
      const columnDef: any = {
        field: column.id,
        headerName: column.label || column.id,
        pinned: isFixed ? 'left' : undefined,
        width: isMultiline ? 200 : (isFixed ? 200 : undefined),
        minWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
        maxWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
        editable: true,
        ...editorConfig,
        valueGetter: (params: any) => {
          const value = params.data[column.id];
          if (value === '-' || value === undefined || value === null) return '';
          if (lookupValueConfig?.valueGetter) {
            return lookupValueConfig.valueGetter(params);
          }
          return value;
        },
        valueSetter: (params: any) => {
          let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
          if (lookupValueConfig?.valueSetter) {
            return lookupValueConfig.valueSetter(params);
          }
          params.data[column.id] = value;
          return true;
        },
        cellRenderer: (params: any) => {
          let displayValue = params.value || '-';
          const renderer = lookupRenderer;
          if (renderer && displayValue !== '-') {
            displayValue = renderer(params) || '-';
          }
          const sourceInfo = params.data?.[`${column.id}_sourceInfo`];
          return showIndicators
            ? createCellWithIndicator(displayValue, sourceInfo, currentDocumentID)
            : displayValue;
        },
        suppressSizeToFit: isMultiline || isFixed,
        suppressAutoSize: isMultiline || isFixed,
        sortable: false,
        filter: false,
        resizable: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        cellStyle: isMultiline || isFixed ? { 
          'white-space': 'normal',
          'word-wrap': 'break-word',
          'overflow-wrap': 'break-word',
          'padding': '8px 8px'
        } : {
          'white-space': 'nowrap',
          'overflow': 'hidden',
          'text-overflow': 'ellipsis',
          'padding': '8px 8px'
        }
      };

      if (children) {
        columnDef.children = children;
      }

      return columnDef;
    }).filter(Boolean);

    const combinedColumns = [...derivedGroupColumn, ...derivedLevelColumns, ...dataColumns];
    const seenHeaders = new Set<string>();
    return combinedColumns.filter((col: any) => {
      const headerKey = typeof col.headerName === 'string' ? col.headerName.toLowerCase() : '';
      if (headerKey === 'group' || headerKey === (primaryLevel?.label || 'group').toLowerCase()) {
        if (seenHeaders.has(headerKey)) {
          return false;
        }
        seenHeaders.add(headerKey);
      }
      return true;
    });
  }

  function createGrouperColumnDefs(levels: any[]): any[] {
    const columnDefs: any[] = [
      {
        field: 'row',
        headerName: 'ROW',
        editable: false,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => params.value ?? '-',
        cellStyle: {
          padding: '8px 8px',
          'text-align': 'center'
        }
      },
      {
        field: 'rowId',
        headerName: 'ROW ID',
        editable: false,
        width: 100,
        minWidth: 100,
        maxWidth: 120,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => params.value ?? '-',
        cellStyle: {
          padding: '8px 8px',
          'text-align': 'center'
        }
      }
    ];

    const levelEntries = levels.map((level: any, index: number) => ({
      levelNumber: String(level.level ?? level.number ?? index + 1),
      label: level.label || level.id || `Level ${index + 1}`,
      sources: (level.sources || []).filter((source: any) => source?.type === 'reference')
    }));

    const primaryLabel = levelEntries[0]?.label || 'Group';
    columnDefs.push({
      field: 'group',
      headerName: primaryLabel.toUpperCase(),
      width: 220,
      minWidth: 180,
      editable: false,
      cellRenderer: (params: any) => params.value ?? '-',
      sortable: false,
      filter: false,
      resizable: false,
      lockPosition: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      cellStyle: {
        padding: '8px 8px'
      }
    });

    levelEntries.slice(1).forEach((levelEntry) => {
      const fieldName = `level_${levelEntry.levelNumber}`;
      columnDefs.push({
        field: fieldName,
        headerName: (levelEntry.label || `Level ${levelEntry.levelNumber}`).toUpperCase(),
        width: 200,
        minWidth: 180,
        editable: false,
        cellRenderer: (params: any) => params.value ?? '-',
        sortable: false,
        filter: false,
        resizable: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        cellStyle: {
          padding: '8px 8px'
        }
      });
    });

    columnDefs.push({
      field: 'custom',
      headerName: 'CUSTOM',
      width: 220,
      minWidth: 180,
      editable: false,
      cellRenderer: (params: any) => params.value ?? '-',
      sortable: false,
      filter: false,
      resizable: false,
      lockPosition: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      cellStyle: {
        padding: '8px 8px'
      }
    });

    return columnDefs;
  }
  
  function getRowOrder(row: any, fallback: number): number {
    if (typeof row?.order === 'number' && !Number.isNaN(row.order)) {
      return row.order;
    }
    const parsed = parseInt(row?.order ?? '', 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    return fallback;
  }

  function createRowData(rowData: any[], cols: any[], isGrouperGrid: boolean = false): any[] {
    const grouperRows = Array.isArray(grouperData) ? grouperData : [];
    const rowMap = new Map<string, any>();
    const result: any[] = [];

    rowData.forEach((row, index) => {
      const key = String(row.id ?? row.grouperId ?? `row_${index + 1}`);
      rowMap.set(key, row);
    });

    const grouperRowMap = new Map<string, any>();
    grouperRows.forEach((row: any, idx: number) => {
      const rowId = String(row?.grouperId ?? row?.id ?? `row_${idx + 1}`);
      grouperRowMap.set(rowId, row);
    });

    const levelDefinitions = Array.isArray(grouperStructure?.levels) ? grouperStructure.levels : [];
    const additionalLevels = levelDefinitions.slice(1).filter((level: any, index: number) => {
      const levelNumber = String(level?.level ?? level?.number ?? index + 2).toLowerCase();
      return levelNumber !== 'custom';
    });

    const buildRowObject = (
      rowId: string,
      rowIndex: number,
      tableRow: any | undefined,
      grouperRow: any | undefined,
      rowOrder: number
    ) => {
      const rowObj: any = {
        id: rowId,
        row: rowOrder
      };

      if (grouperRow) {
        rowObj.__grouperDisplay = getGroupDisplayValue(grouperRow);

        additionalLevels.forEach((level: any, index: number) => {
          const levelNumber = String(level?.level ?? level?.number ?? index + 2);
          const displayValue = getLevelDisplayName(grouperRow, level, $studyExtractStore) || '';
          rowObj[`__grouperLevel_${levelNumber}`] = displayValue || '-';
        });
      }

      cols.forEach((column) => {
        const cell = tableRow?.cells?.find((c: any) => c.id === column.id);

        if (isGrouperGrid) {
          const columnRefKey = typeof column.id === 'string' ? column.id.toLowerCase() : '';
          if (columnRefKey === 'group') {
            rowObj[column.id] = getGroupDisplayValue(grouperRow);
          }
          return;
        }

        if (column.type === 'group' && column.fields) {
          column.fields.forEach((field: any) => {
            const fieldCell = tableRow?.cells?.find((c: any) => c.id === field.id);
            if (fieldCell) {
              rowObj[field.id] = fieldCell.value || '-';
              if (fieldCell.sourceInfo) {
                rowObj[`${field.id}_sourceInfo`] = fieldCell.sourceInfo;
              }
            }
          });
          return;
        }

        if (!column.refTable) {
          rowObj[column.id] = cell?.value || '-';
          if (cell?.sourceInfo) {
            rowObj[`${column.id}_sourceInfo`] = cell.sourceInfo;
          }
        }
      });

      return rowObj;
    };

    if (rowData.length > 0) {
      rowData.forEach((row, index) => {
        const rowId = String(row.id ?? row.grouperId ?? `row_${index + 1}`);
        const grouperRow = grouperRowMap.get(rowId) ?? grouperRowMap.get(String(row.grouperId ?? ''));
        const order = grouperRow ? getRowOrder(grouperRow, index + 1) : index + 1;
        result.push(buildRowObject(rowId, index, row, grouperRow, order));
      });
    } else if (grouperRows.length > 0) {
      grouperRows
        .map((row: any, idx: number): { row: any; order: number } => ({
          row,
          order: getRowOrder(row, idx + 1)
        }))
        .sort(
          (
            a: { row: any; order: number },
            b: { row: any; order: number }
          ): number => {
            if (a.order !== b.order) {
              return a.order - b.order;
            }
            const idA = String(a.row?.grouperId ?? a.row?.id ?? '');
            const idB = String(b.row?.grouperId ?? b.row?.id ?? '');
            return idA.localeCompare(idB);
          }
        )
        .forEach(({ row, order }, index) => {
          const rowId = String(row?.grouperId ?? row?.id ?? `row_${index + 1}`);
          result.push(buildRowObject(rowId, index, undefined, row, order));
        });
    }

    return result.sort((a, b) => {
      const aRow = typeof a.row === 'number' ? a.row : parseFloat(a.row) || 0;
      const bRow = typeof b.row === 'number' ? b.row : parseFloat(b.row) || 0;
      return aRow - bRow;
    });
  }
  
  function initializeGrid() {
    if (!gridDiv) {
      return;
    }
    
    // Destroy existing grid
    if (gridInstance) {
      gridInstance.destroy();
      gridInstance = null;
      gridApi = null;
      gridInitialized = false; // Reset flag when destroying
    }
    
    const { columnDefs, rowData } = createColumnDefsAndRowData();
    
    if (columnDefs.length === 0) {
      logWarning('TopicGrid: No column definitions created, skipping grid initialization', "TopicGrid", undefined);
      return;
    }
    
    if (rowData.length === 0) {
      logWarning('TopicGrid: No row data, but initializing grid with empty data', "TopicGrid", undefined);
    }
    
    // Calculate row height for multiline cells
    const getRowHeight = (params: any) => {
      let maxHeight = 32; // Base row height
      
      // Check each column for multiline content
      columnDefs.forEach((colDef: any) => {
        let isMultilineColumn = colDef.autoHeight === true;

        if (!isMultilineColumn) {
          const cellStyle = colDef.cellStyle;
          isMultilineColumn = Boolean(
            cellStyle && (
              cellStyle['white-space'] === 'normal' ||
              cellStyle['word-wrap'] === 'break-word' ||
              cellStyle['overflow-wrap'] === 'break-word'
            )
          );
        }
        
        if (isMultilineColumn && params.data && colDef.field) {
          const value = params.data[colDef.field];
          if (value && value !== '-' && typeof value === 'string') {
            // Estimate height based on content length
            const columnWidth = colDef.width || colDef.minWidth || 200;
            const charsPerLine = Math.floor(columnWidth / 8);
            const lines = Math.ceil(value.length / charsPerLine);
            const estimatedHeight = 16 + (lines * 19);
            maxHeight = Math.max(maxHeight, estimatedHeight);
          }
        }
      });
      
      return Math.min(maxHeight, 300);
    };
    
    const gridOptions = {
      columnDefs: columnDefs as any,
      rowData,
      domLayout: domLayout as 'normal' | 'autoHeight',
      suppressCellFocus: false,
      stopEditingWhenCellsLoseFocus: true,
      headerHeight: showColumnKeys ? 40 : 32,
      getRowHeight: getRowHeight, // Use getRowHeight for both layouts to handle multiline cells properly
      autoSizeStrategy: {
        type: 'fitCellContents',
      },
      suppressColumnVirtualisation: true,
      suppressRowVirtualisation: true,
      suppressContextMenu: true,
      suppressMovableColumns: true,
      getRowId: (params: any) => {
        const id = params.data?.id || params.node?.id;
        if (!id) {
          logWarning('TopicGrid: getRowId returned undefined for row', "TopicGrid", undefined, { data: params.data });
          return `row_${params.node?.rowIndex || 0}`;
        }
        return String(id);
      },
      components: {
        columnHeaderForTarget: ColumnHeaderForTarget
      },
      defaultColDef: {
        editable: true,
        resizable: false,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true
      },
      onGridReady: (params: any) => {
        gridApi = params.api;
        
        // Initialize content hash after grid is ready (for rows/columns mode)
        if (rows.length > 0 && columns.length > 0 && !data2D && lastRowsContentHash === '') {
          lastRowsContentHash = createRowsContentHash(rows, columns);
          lastRowsLength = rows.length;
        }
      },
      onCellValueChanged: (params: any) => {
        // ALWAYS call the default handler first to update the store and save
        // This ensures data is always persisted, regardless of whether a custom callback is provided
        handleDefaultCellValueChanged(params);
        
        // Then call the custom callback if provided (for additional processing)
        if (onCellValueChanged) {
          onCellValueChanged(params);
        }
      }
    };
    
    gridInstance = createGrid(gridDiv, gridOptions as any);
  }
  
  function handleDefaultCellValueChanged(params: any) {
    if (!$studyExtractStore || !topicId || !tableId || !$projectStructureStore) {
      logWarning('Cannot update table cell - missing required data', "TopicGrid", undefined);
      return;
    }
    
    const { rowIndex, colDef, newValue, data } = params;
    const rowId = data?.id;
    if (!rowId) {
      logWarning(`Cannot find row ID for rowIndex ${rowIndex}`, "TopicGrid", undefined);
      return;
    }
    
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) {
      logWarning(`Topic not found: ${topicId}`, "TopicGrid", undefined);
      return;
    }
    
    const tableStructure = topicStructure.tables?.find((t: any) => t.id === tableId);
    if (!tableStructure) {
      logWarning(`Table not found: ${tableId}`, "TopicGrid", undefined);
      return;
    }
    
    // Find column by matching field or label
    // First check top-level columns
    let columnStructure = tableStructure.columns?.find((col: any) => 
      col.id === colDef.field || 
      col.label === colDef.headerName ||
      col.label === colDef.field
    );
    
    // If not found, check nested fields in group columns
    if (!columnStructure) {
      for (const col of (tableStructure.columns || [])) {
        if (col.type === 'group' && col.fields) {
          const field = col.fields.find((f: any) => 
            f.id === colDef.field ||
            f.label === colDef.headerName ||
            f.label === colDef.field
          );
          if (field) {
            columnStructure = field;
            break;
          }
        }
      }
    }
    
    if (!columnStructure) {
      logWarning(`Column not found for field: ${colDef.field}`, "TopicGrid", undefined, {
        availableColumns: tableStructure.columns?.map((c: any) => ({ id: c.id, type: c.type, hasFields: !!c.fields }))
      });
      return;
    }
    
    if (columnStructure.refTable === 'grouper') {
      logWarning('Attempted to edit grouper column - this should not happen', "TopicGrid", undefined);
      return;
    }
    
    const columnId = columnStructure.id;
    const valueToSave = newValue === '' || newValue === null || newValue === undefined ? '-' : newValue;
    
    const currentUser = $userStore?.user;
    const userId = currentUser?.user_id || currentUser?.oid || 'unknown';
    const userName = currentUser?.name || currentUser?.email || 'Unknown User';
    
    const updatedStudy = updateTargetCell(
      $studyExtractStore,
      topicId,
      tableId,
      rowId,
      columnId,
      valueToSave,
      userId,
      userName
    );
    
    // Update grid row with new sourceInfo BEFORE updating store
    // This prevents the reactive statement from triggering a full reinit
    if (gridApi && params.node) {
      try {
        const updatedTopic = updatedStudy.topics?.find((t: any) => t.id === topicId);
        const updatedTable = updatedTopic?.tables?.find((t: any) => t.id === tableId);
        const updatedRow = updatedTable?.rows?.find((r: any) => r.id === rowId);
        const updatedCell = updatedRow?.cells?.find((c: any) => c.id === columnId);
        
        if (updatedCell?.sourceInfo) {
          // Update the row data with new value and sourceInfo
          const updatedRowData = { 
            ...data,
            id: rowId,
            [colDef.field]: valueToSave, // Update the cell value
            [`${columnId}_sourceInfo`]: updatedCell.sourceInfo // Update sourceInfo
          };
          
          // Use applyTransaction to update the row
          gridApi.applyTransaction({ update: [updatedRowData] });
          
          // Refresh only the affected cell to update indicator and tooltip
          gridApi.refreshCells({ 
            rowNodes: [params.node],
            columns: [colDef.field],
            force: true 
          });
          
        } else {
          // Even if no sourceInfo, update the value
          const updatedRowData = { 
            ...data,
            id: rowId,
            [colDef.field]: valueToSave
          };
          gridApi.applyTransaction({ update: [updatedRowData] });
        }
      } catch (error) {
        logWarning(`Error updating grid row:`, "TopicGrid", undefined, { error: error instanceof Error ? error.message : String(error) });
      }
    }
    
    // Update store AFTER grid update to minimize reactive triggers
    studyExtractStore.set(updatedStudy);
    saveTargetExtract('study', 'study_1', updatedStudy);
  }
  
  // Track if grid has been initialized to prevent unnecessary reinitializations
  let gridInitialized = false;
  let lastColumnsLength = 0;
  let lastRowsLength = 0;
  let lastData2DLength = 0;
  
  // Helper function to create a content hash from rows (includes cell values)
  function createRowsContentHash(rows: any[], columns: any[]): string {
    if (!rows || rows.length === 0) return '';

    const columnsWithGrouperLevels = ensureGrouperColumns(columns);
    const computedRows = createRowData(rows, columnsWithGrouperLevels);

    const hashParts = computedRows.map((row: any) => {
      const rowParts: string[] = [row.id || '', String(row.row ?? '')];

      columnsWithGrouperLevels.forEach((col: any) => {
        const value = row[col.id];
        if (value && typeof value === 'object') {
          rowParts.push(`${col.id}:${JSON.stringify(value)}`);
        } else {
          rowParts.push(`${col.id}:${value ?? ''}`);
        }
        const sourceInfo = row[`${col.id}_sourceInfo`];
        if (sourceInfo) {
          rowParts.push(
            `${col.id}_source:${sourceInfo.sourceType || ''}:${sourceInfo.sourceDate || ''}:${sourceInfo.sourceName || ''}`
          );
        }
      });

      return rowParts.join('|');
    });
    return hashParts.join('||');
  }
  
  let lastRowsContentHash = ''; // Track content hash to detect any changes (structure or values)
  
  // Reactive: reinitialize grid when props change
  // Only reinitialize if data structure changes, not on every store update
  $: if (gridDiv && (columns.length > 0 || (data2D && data2D.length > 0) || rows.length > 0)) {
    const hasColumns = columns.length > 0;
    const hasData2D = data2D && data2D.length > 0;
    const hasRows = rows.length > 0;
    
    // Check if data structure actually changed (not just store reference)
    const columnsChanged = columns.length !== lastColumnsLength;
    const rowsChanged = rows.length !== lastRowsLength;
    const data2DChanged = (data2D?.length || 0) !== lastData2DLength;
    const structureChanged = columnsChanged || rowsChanged || data2DChanged || !gridInitialized;
    
    if (structureChanged) {
    
      lastColumnsLength = columns.length;
      lastRowsLength = rows.length;
      lastData2DLength = data2D?.length || 0;
      
      if (hasColumns || hasData2D) {
        // Initialize content hash for change detection (for rows/columns mode)
        if (rows.length > 0 && columns.length > 0 && !data2D) {
          lastRowsContentHash = createRowsContentHash(rows, columns);
        }
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          initializeGrid();
          gridInitialized = true;
        }, 0);
      } else {
        logWarning('TopicGrid: No data available yet', "TopicGrid", undefined, {
          columnsLength: columns.length,
          rowsLength: rows.length,
          data2DLength: data2D?.length || 0
        });
      }
    }
  }
  
  // Additional reactive statement to handle rows content changes (ExtractItems2 mode)
  // This ensures that when cell values change in the store, the grid updates
  $: if (gridApi && gridInitialized && rows && columns.length > 0 && !data2D) {
    // Only run this for rows/columns mode (not data2D mode)
    // Create a content hash that includes row IDs, cell values, and sourceInfo
    const currentContentHash = createRowsContentHash(rows, columns);
    const rowsLength = rows.length || 0;
    const api = gridApi as GridApi;
    
    // Update if content hash changed (detects both structure changes AND cell value changes)
    // But skip on first run (lastRowsContentHash is empty)
    if (currentContentHash !== lastRowsContentHash && lastRowsContentHash !== '') {
      try {
        const { rowData: newRowData } = createColumnDefsAndRowData();
        if (newRowData && Array.isArray(newRowData)) {
          // Use applyTransaction to update rows in place (avoids flashing)
          // This will update existing rows and add/remove as needed
          const currentRowData: any[] = [];
          api.forEachNode((node: any) => {
            if (node.data) {
              currentRowData.push(node.data);
            }
          });
          
          // Compare current and new row data to determine what changed
          const rowIdsToUpdate = new Set<string>();
          const rowIdsToAdd = new Set<string>();
          const rowIdsToRemove = new Set<string>();
          
          // Find rows to update or add
          newRowData.forEach((newRow: any) => {
            const existingRow = currentRowData.find((r: any) => r.id === newRow.id);
            if (existingRow) {
              // Check if row content actually changed
              const existingHash = JSON.stringify(existingRow);
              const newHash = JSON.stringify(newRow);
              if (existingHash !== newHash) {
                rowIdsToUpdate.add(newRow.id);
              }
            } else {
              rowIdsToAdd.add(newRow.id);
            }
          });
          
          // Find rows to remove
          currentRowData.forEach((row: any) => {
            if (!newRowData.find((r: any) => r.id === row.id)) {
              rowIdsToRemove.add(row.id);
            }
          });
          
          // Apply changes
          const rowsToUpdate = newRowData.filter((r: any) => rowIdsToUpdate.has(r.id));
          const rowsToAdd = newRowData.filter((r: any) => rowIdsToAdd.has(r.id));
          const rowsToRemove = currentRowData.filter((r: any) => rowIdsToRemove.has(r.id));
          
          if (rowsToUpdate.length > 0 || rowsToAdd.length > 0 || rowsToRemove.length > 0) {
            api.applyTransaction({
              update: rowsToUpdate,
              add: rowsToAdd,
              remove: rowsToRemove
            });
            // Refresh updated rows to ensure indicators and tooltips are updated
            if (rowsToUpdate.length > 0) {
              const nodesToRefresh = rowsToUpdate
                .map((row: any) => api.getRowNode(row.id))
                .filter((node: IRowNode<any> | null | undefined): node is IRowNode<any> => !!node);
              if (nodesToRefresh.length > 0) {
                api.refreshCells({ rowNodes: nodesToRefresh, force: true });
              }
            }
          } else {
            // Fallback: if no changes detected but hash changed, refresh all
            api.setGridOption('rowData', newRowData);
          }
          
          lastRowsContentHash = currentContentHash;
          lastRowsLength = rowsLength;
        } else {
          logWarning('TopicGrid: createColumnDefsAndRowData returned invalid data:', "TopicGrid", undefined, { data: newRowData });
        }
      } catch (error) {
        logError(error, "TopicGrid", undefined, { message: 'TopicGrid: Error refreshing grid with new rows' });
        // Reset hash to force retry on next change
        lastRowsContentHash = '';
      }
    } else if (lastRowsContentHash === '' && rowsLength > 0) {
      // Initialize hash on first run
      lastRowsContentHash = currentContentHash;
      lastRowsLength = rowsLength;
    }
  }
  
  // Reactive: update header height when showColumnKeys changes
  $: if (gridApi && showColumnKeys !== undefined) {
    gridApi.setGridOption('headerHeight', showColumnKeys ? 40 : 32);
    // Refresh headers to show/hide keys
    gridApi.refreshHeader();
  }
  
  // Reactive: refresh cells when showIndicators changes
  $: if (gridApi && showIndicators !== undefined) {
    // Refresh all cells to update indicators
    gridApi.refreshCells({ force: true });
  }
  
  onMount(() => {
    
    if (gridDiv) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        initializeGrid();
      }, 0);
    }
  });
  
  onDestroy(() => {
    if (gridInstance) {
      gridInstance.destroy();
      gridInstance = null;
      gridApi = null;
      gridInitialized = false;
    }
  });
</script>

<div 
  bind:this={gridDiv}
  class="ag-theme-quartz ag-topic-grid"
  class:ag-topic-grid-scrollable={domLayout === 'normal'}
  data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"
></div>

